import { DISPLAY_NAME_MAX_LENGTH, type MeDto } from '@spin/shared';
import { type ReactNode, useRef, useState } from 'react';
import { http } from '../api/http';
import { Avatar, useBackButton } from '../components/common';
import { CloseIcon, GmCoin } from '../components/icons';
import { reportError, useT } from '../i18n';
import { squareJpeg } from '../lib/image';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';
import { tg } from '../tg/bridge';
import { LanguagePills } from './Portal';

type Action = 'password' | 'gender' | 'name' | 'photo';

function Modal({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  const t = useT();
  return (
    <div className="dark-modal-layer" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dark-modal" role="dialog" data-testid="services-modal">
        <button className="dark-modal-close" onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon size={14} />
        </button>
        <b>{title}</b>
        {children}
      </div>
    </div>
  );
}

function PasswordForm({ hasPassword, onDone }: { hasPassword: boolean; onDone: () => void }) {
  const t = useT();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const mismatch = repeat.length > 0 && repeat !== password;
  const save = async () => {
    setBusy(true);
    try {
      await http.put('/me/password', hasPassword ? { current, password } : { password });
      const me = useSession.getState().me!;
      useSession.getState().setMe({ ...me, hasPassword: true });
      useUi.getState().toast(t('services.password_saved'), 'success');
      onDone();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="dark-form"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      {hasPassword && (
        <input
          className="dark-input"
          type="password"
          autoComplete="current-password"
          placeholder={t('services.password_current')}
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
      )}
      <input
        className="dark-input"
        type="password"
        autoComplete="new-password"
        placeholder={t('services.password_new')}
        value={password}
        minLength={6}
        maxLength={128}
        onChange={(e) => setPassword(e.target.value)}
        data-testid="new-password"
      />
      <input
        className="dark-input"
        type="password"
        autoComplete="new-password"
        placeholder={t('services.password_repeat')}
        value={repeat}
        onChange={(e) => setRepeat(e.target.value)}
        data-testid="repeat-password"
      />
      <p className="dark-hint">{mismatch ? t('services.password_mismatch') : t('services.password_hint')}</p>
      <button
        className="portal-btn green"
        type="submit"
        disabled={busy || password.length < 6 || password !== repeat || (hasPassword && !current)}
        data-testid="save-password"
      >
        {t('common.save')}
      </button>
    </form>
  );
}

function GenderForm({ me, onDone }: { me: MeDto; onDone: () => void }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const pick = async (gender: 'MALE' | 'FEMALE') => {
    if (gender === me.gender) return onDone();
    setBusy(true);
    try {
      useSession.getState().setMe(await http.patch<MeDto>('/me', { gender }));
      onDone();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };
  const available = me.genderChangeAvailableAt;
  return (
    <div className="dark-form">
      <div className="gender-pick">
        {(['MALE', 'FEMALE'] as const).map((g) => (
          <button
            key={g}
            className={me.gender === g ? 'on' : ''}
            disabled={busy}
            onClick={() => void pick(g)}
            data-testid={`gender-${g}`}
          >
            {g === 'MALE' ? '♂' : '♀'} {t(`gender.${g}`)}
          </button>
        ))}
      </div>
      {available && (
        <p className="dark-hint">
          {t('services.gender_later', { date: new Date(available).toLocaleDateString(me.locale) })}
        </p>
      )}
    </div>
  );
}

function NameForm({ me, onDone }: { me: MeDto; onDone: () => void }) {
  const t = useT();
  const [name, setName] = useState(me.displayName);
  const [busy, setBusy] = useState(false);
  const save = async (displayName: string) => {
    setBusy(true);
    try {
      useSession.getState().setMe(await http.patch<MeDto>('/me', { displayName }));
      onDone();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="dark-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) void save(name.trim());
      }}
    >
      <input
        className="dark-input"
        value={name}
        maxLength={DISPLAY_NAME_MAX_LENGTH}
        onChange={(e) => setName(e.target.value)}
        data-testid="game-name"
      />
      <button
        className="portal-btn green"
        type="submit"
        disabled={busy || !name.trim()}
        data-testid="save-name"
      >
        {t('common.save')}
      </button>
      {me.displayName !== me.firstName && (
        <button className="portal-btn" type="button" disabled={busy} onClick={() => void save(me.firstName)}>
          {t('services.name_telegram', { name: me.firstName })}
        </button>
      )}
    </form>
  );
}

function PhotoForm({ me, onDone }: { me: MeDto; onDone: () => void }) {
  const t = useT();
  const file = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<{ url: string; blob: Blob } | null>(null);
  const [busy, setBusy] = useState(false);
  const pick = async (f: File | undefined) => {
    if (!f) return;
    try {
      const blob = await squareJpeg(f);
      if (preview) URL.revokeObjectURL(preview.url);
      setPreview({ url: URL.createObjectURL(blob), blob });
    } catch {
      useUi.getState().toast(t('errors.UNSUPPORTED_IMAGE'), 'error');
    } finally {
      if (file.current) file.current.value = '';
    }
  };
  const run = async (action: () => Promise<MeDto>) => {
    setBusy(true);
    try {
      useSession.getState().setMe(await action());
      tg.haptic('success');
      if (preview) URL.revokeObjectURL(preview.url);
      onDone();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="dark-form">
      <span className="photo-preview">
        {preview ? (
          <img src={preview.url} alt="" />
        ) : (
          <Avatar name={me.displayName} photoUrl={me.photoUrl} size="100%" />
        )}
      </span>
      <input
        ref={file}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => void pick(e.target.files?.[0])}
        data-testid="photo-input"
      />
      <button
        className="portal-btn"
        disabled={busy}
        onClick={() => file.current?.click()}
        data-testid="pick-photo"
      >
        {t('services.photo_pick')}
      </button>
      {preview && (
        <button
          className="portal-btn green"
          disabled={busy}
          onClick={() => void run(() => http.put<MeDto>('/me/photo', preview.blob))}
          data-testid="save-photo"
        >
          {t('common.save')}
        </button>
      )}
      {me.photoCustom && !preview && (
        <button
          className="portal-btn"
          disabled={busy}
          onClick={() => void run(() => http.delete<MeDto>('/me/photo'))}
        >
          {t('services.photo_telegram')}
        </button>
      )}
    </div>
  );
}

/**
 * Services (the reference's "Store" page): GM balance, the account (username, game name, id) and the profile
 * actions: password for the web portal, gender, game name, profile picture.
 */
export function Services() {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const back = useSession((s) => s.back);
  const [action, setAction] = useState<Action | null>(null);
  useBackButton(back);
  const close = () => setAction(null);

  const actions: { id: Action; icon: string; title: string; text: string }[] = [
    { id: 'password', icon: '🔒', title: t('services.password'), text: t('services.password_text') },
    { id: 'gender', icon: '⚧', title: t('services.gender'), text: t('services.gender_text') },
    { id: 'name', icon: '✏️', title: t('services.name'), text: t('services.name_text') },
    { id: 'photo', icon: '🖼️', title: t('services.photo'), text: t('services.photo_text') },
  ];

  return (
    <div className="portal store" data-testid="services">
      <div className="portal-page">
        <LanguagePills />
        <h1 className="store-title">{t('services.store')}</h1>
        <div className="store-balance" data-testid="store-gm">
          {t('services.balance')} <b>{me.gm}</b> <GmCoin size={18} />
        </div>
        <div className="store-account">
          <div>
            <span aria-hidden>👤</span>
            <small>{t('services.username')}</small>
            <b className="ellipsis">{me.username ?? '-'}</b>
          </div>
          <div>
            <span aria-hidden>🎮</span>
            <small>{t('services.game_name')}</small>
            <b className="ellipsis">{me.displayName}</b>
          </div>
          <div>
            <span className="id-icon" aria-hidden>
              ID
            </span>
            <small>ID</small>
            <b className="mono" data-testid="store-id">
              {me.gameId}
            </b>
          </div>
        </div>
        <h2 className="store-subtitle">{t('services.profile')}</h2>
        <div className="store-actions">
          {actions.map((a) => (
            <button
              key={a.id}
              className="store-action"
              onClick={() => setAction(a.id)}
              data-testid={`service-${a.id}`}
            >
              <span className="store-action-icon" aria-hidden>
                {a.icon}
              </span>
              <span>
                <b>{a.title}</b>
                <small>{a.text}</small>
              </span>
            </button>
          ))}
        </div>
        <button className="portal-btn red back" onClick={back} data-testid="services-back">
          {t('services.back')}
        </button>
      </div>
      {action === 'password' && (
        <Modal title={t('services.password')} onClose={close}>
          <PasswordForm hasPassword={me.hasPassword} onDone={close} />
        </Modal>
      )}
      {action === 'gender' && (
        <Modal title={t('services.gender')} onClose={close}>
          <GenderForm me={me} onDone={close} />
        </Modal>
      )}
      {action === 'name' && (
        <Modal title={t('services.name')} onClose={close}>
          <NameForm me={me} onDone={close} />
        </Modal>
      )}
      {action === 'photo' && (
        <Modal title={t('services.photo')} onClose={close}>
          <PhotoForm me={me} onDone={close} />
        </Modal>
      )}
    </div>
  );
}
